import { useState } from "react";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/Common";
import { fetcher } from "@/lib/api";
import { fmtNum, fmtTime } from "@/lib/format";

const PAGE = 50;
const SEV = {
  info: "bg-secondary text-muted-foreground",
  warning: "bg-[hsl(var(--warn)/0.12)] text-[hsl(var(--warn))]",
  critical: "bg-[hsl(var(--bad)/0.1)] text-[hsl(var(--bad))]",
};

export default function Audit() {
  const [severity, setSeverity] = useState("all");
  const [page, setPage] = useState(0);
  const q = new URLSearchParams({ limit: PAGE, skip: page * PAGE });
  if (severity !== "all") q.set("severity", severity);
  const { data } = useSWR(`/audit?${q}`, fetcher, { refreshInterval: 30000 });
  const pages = data ? Math.ceil(data.total / PAGE) : 0;
  return (
    <div>
      <PageHeader title="Audit log" subtitle="Every sign-in, key change, mapping change and download, with who, what and when.">
        <Select value={severity} onValueChange={(v) => { setSeverity(v); setPage(0); }}>
          <SelectTrigger className="w-40 rounded-full" data-testid="audit-log-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="warning">Warnings</SelectItem>
          </SelectContent>
        </Select>
      </PageHeader>
      <div className="surface overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="audit-log-table">
            <TableHeader>
              <TableRow><TableHead>When</TableHead><TableHead>Event</TableHead><TableHead>Target</TableHead><TableHead>User</TableHead><TableHead>IP</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {(data?.items || []).map((e, i) => (
                <TableRow key={i} data-testid="audit-log-row">
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtTime(e.at)}</TableCell>
                  <TableCell><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${SEV[e.severity] || SEV.info}`}>{e.action.replace(/_/g, " ")}</span></TableCell>
                  <TableCell className="font-mono text-xs">{e.target}</TableCell>
                  <TableCell className="text-xs">{e.user_email}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{e.ip}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between border-t border-border/70 px-5 py-3 text-xs text-muted-foreground">
          <span className="num">{fmtNum(data?.total)} events</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} className="rounded-full" data-testid="audit-prev-page-button">Previous</Button>
            <span className="num">{pages ? page + 1 : 0} / {pages}</span>
            <Button variant="ghost" size="sm" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="rounded-full" data-testid="audit-next-page-button">Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
